import { useMutation } from '@apollo/client';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { format, parse } from 'date-fns';
import { FC, useState } from 'react';
import { Collapse } from 'react-collapse';
import { CourseEnrollmentInfo } from '../../../../components/course-enrollment-info';
import Loader from '../../../../components/loader';
import ModalConfirm from '../../../../components/modal/confirm';
import SignupCode from '../../../../components/signupcode';
import {
  ADD_PATIENT_THERAPY,
  AddPatientTherapyInput,
  DISCHARGE_PATIENT,
  IInvitePatientArgs,
  PATIENT_INVITE,
  TherapyType,
} from '../../../../graphql/mutation/patients';
import useToaster from '../../../../hooks/toast';
import { GraphQLResponse, PATIENT_PROGRESS, PatientView } from '../../../../types';
import { logger } from '../../../../utils/logger';
import { getPatientFullName } from '../../../../utils/name';
import mapProgressLabel from '../../../../utils/patient/mapProgressLabel';
import styles from '../styles.module.css';
import { Course } from './collapsibles/course';
import ProgressDropdown from './progress';
import ProgressLabel from './ProgressLabel';
import RemindPatientSignup from './remindPatientSignup';

interface IPatientRow {
  patient: PatientView;
  onUpdate: () => void;
}

export enum TREATMENT_TYPE {
  PREPARATION_FOR_THERAPY = 'Preparation for Therapy',
  GROUP_THERAPY = 'Group Therapy',
  ONE_TO_ONE_THERAPY = '1:1 Therapy',
}

const TreatmentTypeStatusMap = {
  [TREATMENT_TYPE.PREPARATION_FOR_THERAPY]: TherapyType.preparationForTherapy,
  [TREATMENT_TYPE.GROUP_THERAPY]: TherapyType.groupTherapy,
  [TREATMENT_TYPE.ONE_TO_ONE_THERAPY]: TherapyType.oneToOneTherapy,
};

const Patient: FC<IPatientRow> = props => {
  const { patient, onUpdate } = props;

  const { dob, signupCode, progress, clinic, clinician: patientClinician } = patient;

  const [isOpenConfirm, setIsOpenConfirm] = useState(false);
  const [isOpenRemindSignup, setIsOpenRemindSignup] = useState(false);
  const [isTreatmentFormOpen, setIsTreatmentFormOpen] = useState(false);

  const { success, danger } = useToaster();


  const [dischargePatient, { loading }] = useMutation<
    GraphQLResponse<'dischargePatient', PatientView>,
    { signupCode: string }
  >(DISCHARGE_PATIENT);

  const [invitePatient] = useMutation<
    GraphQLResponse<'invitePatientViaDynamicLink', IInvitePatientArgs>
  >(PATIENT_INVITE);

  const [addPatientTherapy] = useMutation<
    GraphQLResponse<'addPatientTherapy', PatientView>,
    { input: AddPatientTherapyInput }
  >(ADD_PATIENT_THERAPY);

  const handleCollapse = () => {
    setIsTreatmentFormOpen(false);
  };

  const handleCloseConfirm = () => {
    setIsOpenConfirm(false);
  };

  const getDateFormatted = (input?: string): string => {
    if (!input) return 'Unknown';
    return format(parse(input, 'yyyy-MM-dd', new Date()), 'dd-MM-yyyy');
  };

  const handleConfirmDischarge = async () => {
    setIsOpenConfirm(false);
    try {
      await dischargePatient({
        variables: {
          signupCode,
        },
      });
      success('Patient discharged successfully');
      onUpdate();
    } catch (err: any) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      danger(message);
      err.message = `Error discharging patient: ${message}`;
      logger.error(err);
      onUpdate();
    }
  };

  const handleClickSendInvite = () => {
    setIsOpenRemindSignup(true);
  };

  const handleCloseRemindSignup = () => {
    setIsOpenRemindSignup(false);
  };

  const handleAddPatientTherapy = async ({
    courseId,
    startDate,
    sendSms,
    clinicianId,
    treatmentType,
  }: {
    courseId: string;
    startDate: Date;
    sendSms: boolean;
    clinicianId?: string;
    treatmentType: TREATMENT_TYPE;
  }) => {
    try {
      const input: AddPatientTherapyInput = {
        type: TreatmentTypeStatusMap[treatmentType],
        signupCode,
        courseEnrollments: [],
        protocolEnrollments: [],
        clinicianId: clinicianId && patientClinician?.id !== clinicianId ? clinicianId : undefined,
        allowFirstInteractionInThePast: true,
      };

      if (treatmentType === TREATMENT_TYPE.ONE_TO_ONE_THERAPY) {
        input.protocolEnrollments.push({
          protocolId: courseId,
          startDate: format(startDate, 'yyyy-MM-dd'),
        });
      } else {
        input.courseEnrollments.push({
          courseId,
          startDate: format(startDate, 'yyyy-MM-dd'),
        });
      }

      await addPatientTherapy({
        variables: { input },
      });

      if (sendSms) {
        await invitePatient({
          variables: {
            signupCode,
            overrideResend: true,
          },
        });
      }

      success('Patient enrolled in course');
    } catch (err: any) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      danger(message);
      err.message = `Error setting patient therapy: ${message}`;
      logger.error(err);
    } finally {
      onUpdate();
    }
  };

  const onAddTreatment = () => {
    setIsTreatmentFormOpen(true);
  };

  const onDischarge = () => {
    setIsOpenConfirm(true);
  };

  return (
    <div className={styles.row}>
      <div className={styles.rowLevel}>
        <div className={styles.rowLeft}>
          <div className={clsx(styles.rowItem, styles.rowItemCollapse)}>
            <div className={styles.envelopeIconDiv} onClick={handleClickSendInvite}>
              <FontAwesomeIcon
                icon={faEnvelope as IconProp}
                className={styles.envelopeIcon}
                title="Send invite"
              />
            </div>
          </div>
          <div className={clsx(styles.rowItem, styles.rowItemExpand, styles.label)}>
            {patient.clinician && (
              <span className={styles.textMuted}>Assigned to {patient.clinician.name}</span>
            )}
            {patient.courseEnrollments.length > 0 && (
              <span className={styles.textMuted}>
                <CourseEnrollmentInfo
                  courseEnrollments={patient.courseEnrollments}
                  progress={progress}
                />
              </span>
            )}
            <SignupCode code={signupCode} />
            <span className={styles.textMuted}>{getPatientFullName(patient)}</span>
          </div>
          <div className={clsx(styles.rowItem, styles.textMuted)}>{getDateFormatted(dob)}</div>
        </div>
        <div className={styles.rowRight}>
          <div className={clsx(styles.rowItem, styles.textMuted)}>
            {clinic?.displayName || null}
          </div>
          <div className={styles.labelRow}>
            <div>
              <ProgressLabel progressLabel={mapProgressLabel(patient)} />
              {progress !== PATIENT_PROGRESS.DISCHARGED && (
                <ProgressDropdown
                  progress={progress}
                  onAddTreatment={onAddTreatment}
                  onDischarge={onDischarge}
                  triggerProps={{}}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <Loader loading={loading} />

      <Collapse
        isOpened={isTreatmentFormOpen}
        theme={{
          collapse: styles.collapse,
          content: styles.collapseContent,
        }}>
        <Course
          existingClinicianId={patientClinician?.id}
          onCancel={handleCollapse}
          onConfirm={handleAddPatientTherapy}
        />
      </Collapse>

      {/* this should be moved to a provider or something and reused */}
      <ModalConfirm
        title="This action can't be undone"
        isOpen={isOpenConfirm}
        onClose={handleCloseConfirm}
        onConfirm={handleConfirmDischarge}
        confirmText="Discharge">
        <p className={styles.modalBodyText}>
          Please make sure the Limbic Code matches the Limbic Code in the patient management system.
        </p>
      </ModalConfirm>

      <RemindPatientSignup
        isOpen={isOpenRemindSignup}
        onClose={handleCloseRemindSignup}
        patient={patient}
      />
    </div>
  );
};

export default Patient;
